import store from '../store'
import { isOld } from '../../helpers/isOld'
import { PRODUCT_PAGE } from '../../constants/constants'
import { reportMissingArticles } from '../articleSubstitutions/articleSubstitutionsSlice'
import { type APIConfig } from './apiTypes'
import type * as DynamakerContract from '../../integration/DynamakerContract'
import { type PostLfcConfigresultList } from './apiRequests/postLfcConfigresultList'

// Recognised by fetchPrices_aggregated, which turns it into a flag on the rejected payload
export const AWAITING_SUBSTITUTIONS_ABORT = 'Aborting: awaiting article substitutions from DynaMaker'

interface PostLfcConfigresultListItem {
  contextId: string
  chosenValues: number[]
  pricingInfo: any
}

export const completeExecutionWithUpToDateBom = async <T = any>(
  bom: DynamakerContract.BOM,
  erpItems: any[],
  apiConfigs: APIConfig,
  postLfcConfigresultList: PostLfcConfigresultList,
  promiseCreator: (bom: DynamakerContract.BOM, updatedItems: DynamakerContract.ConfiguredArticleQty[]) => Promise<T>
): Promise<T> => {
  const bomItemsWithOldConfigData: DynamakerContract.ConfiguredArticleQty[] = []

  bom.forEach((section) =>
    section.articles.forEach((article) => {
      if ('configData' in article && article.configData) {
        if (!article.configData._lastFetched || !article.configData.contextId || !article.configData.chosenValues) {
          console.warn(
            `completeExecutionWithUpToDateBom: unexpected configData in bom item: ${JSON.stringify(article)}`
          )
        } else if (isOld(article.configData._lastFetched)) {
          bomItemsWithOldConfigData.push(article)
        }
      }
    })
  )

  if (bomItemsWithOldConfigData.length === 0) return promiseCreator(bom, [])

  const missingErpItemNumbers: string[] = []

  const postLfcConfigresultListItems: PostLfcConfigresultListItem[] = bomItemsWithOldConfigData.flatMap((bomItem) => {
    const matchingErpItem = erpItems.find(
      // TODO lägg tillbaka när bugg rättats i Scensum
      // (erpItem) => erpItem.ContextId === bomItem.configData.contextId
      (erpItem) => erpItem.Sku === bomItem.configData.contextId
    )
    if (!matchingErpItem) {
      console.error(
        'No article in response data from Scensum /articleinfolist with ContextId matching bomItem.configData.contextId:',
        bomItem.configData.contextId
      )
      if (bomItem.erpItemNumber) {
        missingErpItemNumbers.push(bomItem.erpItemNumber)
      }
      return []
    }
    // TODO lägg tillbaka när bugg rättats i Scensum
    // if (!matchingErpItem.ContextId || !matchingErpItem.PricingInfo) {
    //   console.error(
    //     `Mallis article info is missing in response data from Scensum /articleinfolist for Sku ${matchingErpItem.Sku} -- ContextId is ${matchingErpItem.ContextId}, PricingInfo is ${matchingErpItem.PricingInfo}`
    //   );
    //   return [];
    // }
    // return [{
    //   contextId: matchingErpItem.ContextId,

    return [
      {
        contextId: bomItem.configData.contextId,
        chosenValues: bomItem.configData.chosenValues,
        pricingInfo: matchingErpItem.PricingInfo,
      },
    ]
  })

  // The product page has no DynaMaker to ask for substitutes, and nothing there would ever release the wait
  if (missingErpItemNumbers.length && store.getState().init.applicationMode !== PRODUCT_PAGE) {
    // Everything here comes from the bom, so it is by definition part of the configuration
    store.dispatch(reportMissingArticles({ knownMissing: missingErpItemNumbers, toReport: missingErpItemNumbers }))

    // Only abort while DM is working on it; an already reported article it couldn't replace must not stall us forever
    if (store.getState().articleSubstitutions.isAwaitingArticleSubstitutions) {
      throw new Error(AWAITING_SUBSTITUTIONS_ABORT)
    }
  }

  const aggregatedResponse = await postLfcConfigresultList({
    apiConfigs,
    data: {
      list: postLfcConfigresultListItems,
    },
    onSuccess: async (response) => {
      const _lastFetched = Date.now()

      const updatedItemsPerKey = new Map<string, any>()
      response.data.list.forEach((item) =>
        updatedItemsPerKey.set(`${item.contextId}:${JSON.stringify(item.chosenValues)}`, item)
      )

      const updatedItems: DynamakerContract.ConfiguredArticleQty[] = []

      const updatedBom = bom.map((section) => ({
        ...section,
        articles: section.articles.map((article) => {
          if ('configData' in article && article.configData) {
            const updatedItem = updatedItemsPerKey.get(
              `${article.erpItemNumber}:${JSON.stringify(article.configData.chosenValues)}`
            )

            // Articles that were fresh, or left out of the request, have no entry here -- overwriting them nulls configResult
            if (!updatedItem) return { ...article }

            const updatedArticle: DynamakerContract.ConfiguredArticleQty = {
              ...article,
              configData: {
                ...article.configData,
                _lastFetched,
                configResult: updatedItem.configResult,
              },
            }
            updatedItems.push(updatedArticle)
            return updatedArticle
          }

          return { ...article }
        }),
      }))

      return promiseCreator(updatedBom, updatedItems)
    },
  })

  return aggregatedResponse
}
